import _ from "lodash";
import { QUERY_STATUS, SCOPES } from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createMissionBillQuery,
  getMissionBillByMissionIdQuery,
  bulkEditMissionBillQuery,
  searchMissionBillsByParametersQuery,
  importExcelFacturationsQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { MISSION_ROLES, ROLE_GET_MISSION_BILL } from "access-roles/roles";
import { canUserPerformAction } from "access-roles/general-functions";

const MissionBillContext = createContext({});

export const useMissionBillContext = () => useContext(MissionBillContext);

export const MissionBillProvider = ({ ...props }) => {
  const [total, setTotal] = useState();
  const [missionBillByMissionId, setMissionBillByMissionId] = useState([]);
  const [missionBillById, setMissionBillById] = useState([]);
  const [missionBillsByParameters, setMissionBillsByParameters] = useState([]);
  const [progression, setProgression] = useState(0);
  const [serverResponse, setServerResponse] = useState({});
  const [importExcelFacturationsStatus, setImportExcelFacturationsStatus] =
    useState(QUERY_STATUS.INITIAL);
  const [loadMissionStatus, setLoadMissionStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [bulkEditMissionBillStatus, setBulkEditMissionBillStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [selectedDateObj, setSelectedDateObj] = useState({});
  const { userScope, userRoleList } = useUserContext();

  const createMissionBill = useCallback(
    async ({ data }) => {
      try {
        await createMissionBillQuery({ data, userScope });
      } catch (error) {
        console.error(error);
      }
    },
    [userScope]
  );

  const getMissionBillByMissionId = useCallback(
    async (id) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_MISSION_BILL,
        })
      ) {
        return;
      }
      try {
        const result = await getMissionBillByMissionIdQuery({ id, userScope });
        setMissionBillByMissionId(result);
      } catch (error) {
        console.error(error);
      }
    },
    [userScope, userRoleList]
  );

  const searchMissionBillsByParameters = useCallback(
    async (filters) => {
      try {
        setLoadMissionStatus(QUERY_STATUS.LOADING);
        const result = await searchMissionBillsByParametersQuery({
          filters,
          scope: userScope,
        });
        setLoadMissionStatus(QUERY_STATUS.SUCCESS);
        setMissionBillsByParameters(result?.bills || []);
        setTotal(result?.total_pages);
      } catch (error) {
        setLoadMissionStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userScope]
  );

  const bulkEditMissionBill = useCallback(
    async ({ data }) => {
      const requiredRole =
        userScope === SCOPES.ADMIN
          ? MISSION_ROLES.ROLE_ADMIN_MISSION_BILL
          : MISSION_ROLES.ROLE_UPDATE_MISSION_BILL;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: requiredRole,
        })
      ) {
        return;
      }
      try {
        setBulkEditMissionBillStatus(QUERY_STATUS.LOADING);
        const result = await bulkEditMissionBillQuery({ data, userScope });
        setMissionBillByMissionId((old) =>
          _.map(old, (element) => {
            const existingIndex = _.findIndex(result, { id: element.id });
            if (existingIndex === -1) {
              return element;
            } else {
              return _.nth(result, existingIndex);
            }
          })
        );
        setBulkEditMissionBillStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        console.error(error);
        setBulkEditMissionBillStatus(QUERY_STATUS.ERROR);
      }
    },
    [missionBillByMissionId, userScope, userRoleList]
  );

  const importExcelFacturations = useCallback(
    async ({ data }) => {
      const requiredRole =
        userScope === SCOPES.ADMIN
          ? MISSION_ROLES.ROLE_ADMIN_MISSION_BILL
          : MISSION_ROLES.ROLE_UPDATE_MISSION_BILL;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: requiredRole,
        })
      ) {
        return;
      }
      setImportExcelFacturationsStatus(QUERY_STATUS.LOADING);
      if (data) {
        try {
          const result = await importExcelFacturationsQuery({
            data,
            setProgression,
            userScope,
          });
          result?.total && setServerResponse(result);
          return setImportExcelFacturationsStatus(QUERY_STATUS.SUCCESS);
        } catch (e) {
          console.error("importExcelFacturations", e);
          setServerResponse(e.message);
          return setImportExcelFacturationsStatus(QUERY_STATUS.ERROR);
        }
      }
    },
    [userScope, userRoleList]
  );

  const values = useMemo(
    () => ({
      createMissionBill,
      missionBillByMissionId,
      getMissionBillByMissionId,
      missionBillById,
      bulkEditMissionBill,
      searchMissionBillsByParameters,
      missionBillsByParameters,
      total,
      loadMissionStatus,
      selectedDateObj,
      setSelectedDateObj,
      userScope,
      importExcelFacturationsStatus,
      setImportExcelFacturationsStatus,
      importExcelFacturations,
      progression,
      setProgression,
      serverResponse,
      setServerResponse,
      bulkEditMissionBillStatus,
      setBulkEditMissionBillStatus,
    }),
    [
      userScope,
      selectedDateObj,
      setSelectedDateObj,
      createMissionBill,
      missionBillByMissionId,
      getMissionBillByMissionId,
      missionBillById,
      bulkEditMissionBill,
      searchMissionBillsByParameters,
      missionBillsByParameters,
      total,
      loadMissionStatus,
      importExcelFacturationsStatus,
      setImportExcelFacturationsStatus,
      importExcelFacturations,
      progression,
      setProgression,
      serverResponse,
      setServerResponse,
      bulkEditMissionBillStatus,
      setBulkEditMissionBillStatus,
    ]
  );
  return <MissionBillContext.Provider value={values} {...props} />;
};
